/**
 * Crew & capacity output view — ports CrewCapacity.jsx from the design handoff.
 * One card per Active sync group: member line tracks (OWeekTrack) + load ribbon
 * (run-length encoded by concurrent count vs maxActive) + footer stats.
 */
import { memo, useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { TOKENS, TYPE, FONT_MONO, FONT_SANS } from './SchedulerDesign'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { SynchronizedLinesRowType } from './Scheduler.types'

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const pct = (h: number) => `${(h / TOTAL_HOURS) * 100}%`

const BAR_COLOR: Record<string, string> = {
  production: TOKENS.ganttProduction,
  clean: TOKENS.ganttClean,
  preclean: TOKENS.ganttPreClean,
}

// ─── Shared layout primitives ─────────────────────────────────────────────────

const OWeekTrack = memo(
  ({
    height = 22,
    bg = TOKENS.ganttIdle,
    children,
  }: {
    height?: number
    bg?: string
    children?: React.ReactNode
  }) => (
    <Box
      sx={{
        position: 'relative',
        height,
        bgcolor: bg,
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '3px',
        overflow: 'hidden',
      }}
    >
      {[1, 2, 3, 4, 5, 6].map((d) => (
        <Box
          key={d}
          sx={{
            position: 'absolute',
            left: pct(d * 24),
            top: 0,
            bottom: 0,
            width: '1px',
            bgcolor: 'rgba(14,27,34,0.12)',
            zIndex: 1,
          }}
        />
      ))}
      {children}
    </Box>
  )
)
OWeekTrack.displayName = 'OWeekTrack'

const OBand = memo(
  ({
    start,
    end,
    color,
    label,
    title: ttl,
    radius = 2,
    top = 2,
    bottom = 2,
  }: {
    start: number
    end: number
    color: string
    label?: string
    title?: string
    radius?: number
    top?: number
    bottom?: number
  }) => {
    const width = Math.max(0, Math.min(TOTAL_HOURS, end) - Math.max(0, start))
    if (width <= 0) return null
    return (
      <Tooltip title={ttl ?? ''} arrow disableHoverListener={!ttl}>
        <Box
          sx={{
            position: 'absolute',
            left: pct(Math.max(0, start)),
            width: pct(width),
            top,
            bottom,
            zIndex: 2,
            bgcolor: color,
            borderRadius: radius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {label && (
            <Box
              component="span"
              sx={{
                fontSize: 8.5,
                fontWeight: 700,
                color: '#fff',
                fontFamily: FONT_MONO,
                whiteSpace: 'nowrap',
                px: '3px',
              }}
            >
              {label}
            </Box>
          )}
        </Box>
      </Tooltip>
    )
  }
)
OBand.displayName = 'OBand'

const WeekDayHeader = memo(() => (
  <Box sx={{ display: 'flex', mb: '4px' }}>
    {DAY_LABELS.map((d) => (
      <Box
        key={d}
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: TOKENS.ink400,
        }}
      >
        {d}
      </Box>
    ))}
  </Box>
))
WeekDayHeader.displayName = 'WeekDayHeader'

// ─── PanelHead ────────────────────────────────────────────────────────────────

type PanelMetric = {
  label: string
  value: number | string
  unit?: string
  tone?: string
}

const PanelHead = memo(
  ({
    icon,
    title,
    sub,
    metrics,
  }: {
    icon: React.ReactNode
    title: string
    sub?: string
    metrics?: PanelMetric[]
  }) => (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${TOKENS.line200}`,
        px: '18px',
        py: '11px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <Box
        sx={{
          color: TOKENS.stateComplete,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18 }}>
        <Box sx={{ ...TYPE.h2 }}>{title}</Box>
        {sub && <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>{sub}</Box>}
      </Box>
      <Box sx={{ flex: 1 }} />
      {metrics && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {metrics.map((m, i) => (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                pr: i < metrics.length - 1 ? '18px' : 0,
                borderRight:
                  i < metrics.length - 1
                    ? `1px solid ${TOKENS.line200}`
                    : 'none',
                mr: i < metrics.length - 1 ? '18px' : 0,
              }}
            >
              <Box
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: TOKENS.ink400,
                }}
              >
                {m.label}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: m.tone ?? TOKENS.ink900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
                {m.unit && (
                  <Box
                    component="span"
                    sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                  >
                    {m.unit}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
)
PanelHead.displayName = 'PanelHead'

// ─── Data types & derivation ──────────────────────────────────────────────────

type CrewGroupData = {
  id: number
  name: string
  lines: string[]
  maxActive: number
  loadBands: { start: number; end: number; count: number }[]
  peak: number
  overHours: number
}

function deriveCrewGroups(
  syncGroups: SynchronizedLinesRowType[],
  outputRows: InputRow[]
): CrewGroupData[] {
  return syncGroups
    .filter((g) => String(g.productMasterType).toLowerCase() === 'active')
    .map((g) => {
      const lines = String(g.lines ?? '')
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
      const maxActive = Number(g.active) || 1
      const hourly = new Array(TOTAL_HOURS).fill(0)
      for (const row of outputRows) {
        if (!lines.includes(row.line)) continue
        const s = Math.max(0, Math.floor(row.line_start_time ?? 0))
        const e = Math.min(TOTAL_HOURS, Math.ceil(row.line_end_time ?? 0))
        for (let h = s; h < e; h++) hourly[h]++
      }
      const bands: { start: number; end: number; count: number }[] = []
      let cur: { start: number; end: number; count: number } | null = null
      for (let h = 0; h <= TOTAL_HOURS; h++) {
        const c = h < TOTAL_HOURS ? hourly[h] : -1
        if (cur === null) {
          if (c > 0) cur = { start: h, end: h + 1, count: c }
        } else if (c !== cur.count) {
          bands.push(cur)
          cur = c > 0 ? { start: h, end: h + 1, count: c } : null
        } else cur.end = h + 1
      }
      return {
        id: g.id,
        name: g.groupName,
        lines,
        maxActive,
        loadBands: bands,
        peak: Math.max(0, ...hourly),
        overHours: hourly.filter((v) => v > maxActive).length,
      }
    })
}

function getLineBars(lineName: string, outputRows: InputRow[]) {
  const bars: { start: number; end: number; type: string }[] = []
  const seen = new Set<string>()
  for (const row of outputRows) {
    if (row.line !== lineName) continue
    const add = (
      s: number | null | undefined,
      e: number | null | undefined,
      type: string
    ) => {
      if (s == null || e == null) return
      const k = `${type}-${s}-${e}`
      if (!seen.has(k)) {
        seen.add(k)
        bars.push({ start: s, end: e, type })
      }
    }
    add(row.line_start_time, row.line_end_time, 'production')
    add(row.line_clean_start_time, row.line_clean_end_time, 'clean')
    add(row.line_preclean_start_time, row.line_preclean_end_time, 'preclean')
  }
  return bars
}

// ─── CrewStat ─────────────────────────────────────────────────────────────────

const CrewStat = memo(
  ({
    label,
    value,
    unit,
    tone,
  }: {
    label: string
    value: string
    unit?: string
    tone?: string
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <Box
        sx={{
          fontSize: 9,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: TOKENS.ink400,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 15,
          fontWeight: 600,
          color: tone ?? TOKENS.ink900,
          lineHeight: 1,
        }}
      >
        {value}
        {unit && (
          <Box
            component="span"
            sx={{ fontSize: 10, color: TOKENS.ink400, ml: '3px' }}
          >
            {unit}
          </Box>
        )}
      </Box>
    </Box>
  )
)
CrewStat.displayName = 'CrewStat'

// ─── CrewCard ─────────────────────────────────────────────────────────────────

const CrewCard = memo(
  ({ group, outputRows }: { group: CrewGroupData; outputRows: InputRow[] }) => {
    const tone =
      group.overHours > 0 ? TOKENS.ganttDowntime : TOKENS.stateComplete
    return (
      <Box
        sx={{
          border: `1px solid ${TOKENS.line200}`,
          borderRadius: '3px',
          bgcolor: '#fff',
          mb: '14px',
          overflow: 'hidden',
        }}
      >
        {/* Card header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '14px',
            py: '11px',
            borderBottom: `1px solid ${TOKENS.line100}`,
            bgcolor: TOKENS.surface50,
          }}
        >
          <GroupsIcon sx={{ fontSize: 17, color: TOKENS.ganttClean }} />
          <Box
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: TOKENS.ink900,
              flex: 1,
              fontFamily: FONT_SANS,
            }}
          >
            {group.name}
          </Box>
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>Max active</Box>
          <Box
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 14,
              fontWeight: 700,
              color: TOKENS.stateComplete,
            }}
          >
            {group.maxActive}
          </Box>
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>lines</Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: '12px 14px 14px' }}>
          {/* Day header */}
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ width: 88, minWidth: 88 }} />
            <Box sx={{ flex: 1 }}>
              <WeekDayHeader />
            </Box>
          </Box>

          {/* Member line tracks */}
          {group.lines.map((lineName) => {
            const bars = getLineBars(lineName, outputRows)
            return (
              <Box
                key={lineName}
                sx={{ display: 'flex', alignItems: 'center', height: 24 }}
              >
                <Box
                  sx={{
                    width: 88,
                    minWidth: 88,
                    textAlign: 'right',
                    pr: '10px',
                    fontSize: 11,
                    fontFamily: FONT_MONO,
                    fontWeight: 500,
                    color: TOKENS.ink600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lineName}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <OWeekTrack height={15}>
                    {bars.map((b, i) => (
                      <OBand
                        key={i}
                        start={b.start}
                        end={b.end}
                        color={BAR_COLOR[b.type] ?? TOKENS.ganttChangeover}
                        radius={2}
                        top={2}
                        bottom={2}
                      />
                    ))}
                  </OWeekTrack>
                </Box>
              </Box>
            )
          })}

          {/* Load ribbon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 30,
              mt: '6px',
              pt: '6px',
              borderTop: `1px solid ${TOKENS.line100}`,
            }}
          >
            <Box
              sx={{
                width: 88,
                minWidth: 88,
                textAlign: 'right',
                pr: '10px',
                fontSize: 11,
                fontFamily: FONT_MONO,
                fontWeight: 700,
                color: tone,
              }}
            >
              Load
            </Box>
            <Box sx={{ flex: 1 }}>
              <OWeekTrack height={20} bg={TOKENS.surface50}>
                {group.loadBands.map((b, i) => {
                  const over = b.count > group.maxActive
                  const atCap = b.count === group.maxActive
                  const color = over
                    ? TOKENS.ganttDowntime
                    : atCap
                      ? TOKENS.ganttClean
                      : TOKENS.ganttProduction
                  return (
                    <OBand
                      key={i}
                      start={b.start}
                      end={b.end}
                      color={color}
                      label={String(b.count)}
                      title={`${b.count} active line${b.count !== 1 ? 's' : ''}${over ? ' — over capacity' : ''}`}
                      radius={0}
                      top={0}
                      bottom={0}
                    />
                  )
                })}
              </OWeekTrack>
            </Box>
          </Box>

          {/* Footer stats */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '18px',
              pl: '88px',
              mt: '9px',
            }}
          >
            <CrewStat
              label="Peak load"
              value={String(group.peak)}
              unit={`/ ${group.maxActive}`}
              tone={
                group.peak > group.maxActive
                  ? TOKENS.ganttDowntime
                  : TOKENS.ink900
              }
            />
            <CrewStat
              label="Over-cap hours"
              value={String(group.overHours)}
              unit="h"
              tone={
                group.overHours ? TOKENS.ganttDowntime : TOKENS.stateComplete
              }
            />
            <CrewStat label="Member lines" value={String(group.lines.length)} />
            <Box sx={{ flex: 1 }} />
            {group.overHours > 0 ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 11.5,
                  color: TOKENS.ganttDowntime,
                  fontWeight: 600,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 15 }} />
                {group.overHours}h above {group.maxActive}-line limit
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 11.5,
                  color: TOKENS.stateComplete,
                  fontWeight: 600,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 15 }} />
                Within crew capacity
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  }
)
CrewCard.displayName = 'CrewCard'

// ─── Main export ──────────────────────────────────────────────────────────────

export const ScheduleCrewUsageView = memo(
  ({
    outputRows,
    synchronizedLines,
  }: {
    outputRows: InputRow[]
    synchronizedLines: SynchronizedLinesRowType[]
  }) => {
    const groups = useMemo(
      () => deriveCrewGroups(synchronizedLines, outputRows),
      [synchronizedLines, outputRows]
    )
    const totalOver = groups.reduce((s, c) => s + c.overHours, 0)
    const breached = groups.filter((c) => c.overHours > 0).length

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<GroupsIcon sx={{ fontSize: 19 }} />}
          title="Crew & capacity"
          sub="Concurrent line load against each crew's limit"
          metrics={[
            { label: 'Crews', value: groups.length },
            {
              label: 'Over-cap hours',
              value: totalOver,
              unit: 'h',
              tone: totalOver ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
            {
              label: 'Breached',
              value: `${breached}/${groups.length}`,
              tone: breached ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box sx={{ overflow: 'auto', px: '18px', py: '16px', pb: '24px' }}>
          {/* Legend */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              mb: '12px',
              flexWrap: 'wrap',
            }}
          >
            {[
              [TOKENS.ganttProduction, 'production'],
              [TOKENS.ganttClean, 'at capacity / CIP'],
              [TOKENS.ganttProduction, 'load (within limit)'],
              [TOKENS.ganttDowntime, 'over capacity'],
            ].map(([c, l]) => (
              <Box
                key={l}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: 10.5,
                  color: TOKENS.ink500,
                }}
              >
                <Box
                  sx={{
                    width: 11,
                    height: 11,
                    borderRadius: '2px',
                    bgcolor: c,
                  }}
                />
                {l}
              </Box>
            ))}
          </Box>

          {groups.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No crew groups configured (no Active-type synchronized lines).
            </Box>
          ) : (
            groups.map((g) => (
              <CrewCard key={g.id} group={g} outputRows={outputRows} />
            ))
          )}
        </Box>
      </Box>
    )
  }
)
ScheduleCrewUsageView.displayName = 'ScheduleCrewUsageView'
